import systemSyncStoreV1 from './store'
import { IStore } from './core/interface/IStore'
import { ISysSyncModule, OperationHandler } from '@/contracts/system-modules/sync/ISysSyncModule'
import { ISysAuthModule } from '@/contracts/system-modules/auth/ISysAuthModule'
import { IOperation } from '@/contracts/system-modules/sync/operation/IOperation'
import { IOperationDraft } from '@/contracts/system-modules/sync/operation/IOperationDraft'
import { IRecordState } from '@/contracts/system-modules/sync/operation/IRecordState'
import { IEventBus } from '@/contracts/core/event/interface/IEventBus'
import { EnumSystemEvent } from '@/contracts/core/event/enum/EnumSystemEvent'
import { EventProviderModule } from '@/core/event/EventProvider'
import { SysAuthProviderModule } from '@/system-modules/auth/SysAuthProvider'
import { PushOperationsRoute } from '@/system-modules/sync/v1/api/v1/sync/PushOperations/PushOperationsRoute'
import { PushOperationsParams } from '@/system-modules/sync/v1/api/v1/sync/PushOperations/Dto/PushOperationsParams'
import { GetOperationsRoute } from '@/system-modules/sync/v1/api/v1/sync/GetOperations/GetOperationsRoute'
import { GetOperationsParams } from '@/system-modules/sync/v1/api/v1/sync/GetOperations/Dto/GetOperationsParams'
import { mergeRecord } from '@/helpers/mergeRecord'
import { IClock, formatOperationTime, isOperationTime, moveClock, nextClock, operationTimeOrigin } from '@/helpers/operationTime'
import {
  putQueued, readClock, readCursor, readQueue, readRecordOperations, removeLegacyStorage, removeQueued,
  saveOperations, writeClock, writeCursor,
} from '@/helpers/syncStorage'
import { readTokenUserUuid } from '@/helpers/tokenStorage'

export const PAGE_SIZE = 200
export const PAGES_PER_RECEIVE = 20

/** Состояние обмена одной учётной записи в памяти; IndexedDB — его копия на случай перезагрузки */
interface IUserState {
  queue: IOperation[]
  clock: IClock
  cursor: number
  loaded: Promise<void>
}

export class SysSyncV1 implements ISysSyncModule {
  private static instance: ISysSyncModule
  private store: IStore = systemSyncStoreV1()
  private events: IEventBus
  private auth: () => ISysAuthModule
  private handlers: Record<string, OperationHandler[]> = {}
  private states: Record<string, IUserState> = {}
  private persisting: Promise<void> = Promise.resolve()
  private exchanging = false

  /**
   * Модуль auth берётся при обращении, а не в конструкторе: auth и sync нужны друг другу,
   * и создание одного из конструктора другого зациклилось бы.
   */
  constructor(
    events: IEventBus = EventProviderModule(),
    auth: () => ISysAuthModule = () => SysAuthProviderModule(),
  ) {
    this.events = events
    this.auth = auth
  }

  public static getInstance(): ISysSyncModule {
    if (!SysSyncV1.instance) {
      SysSyncV1.instance = new SysSyncV1()
    }
    return SysSyncV1.instance
  }

  init(): boolean {
    this.events.on(EnumSystemEvent.LOCKED, () => this.stop())
    this.events.on(EnumSystemEvent.UNLOCKED, () => this.start())
    window.addEventListener('online', () => this._exchange())

    return true
  }

  /** Синхронно: память — источник правды, запись в IndexedDB идёт следом */
  write(draft: IOperationDraft): IOperation {
    const userUuid = this._userUuid()
    const state = this._state(userUuid)
    state.clock = nextClock(state.clock, Date.now())
    const operation: IOperation = {
      time: formatOperationTime(state.clock.millis, state.clock.counter, this.auth().getDeviceUuid()),
      set: draft.set,
      record: draft.record,
      user: userUuid,
      kind: draft.kind,
      event: draft.event,
      fields: draft.fields,
    }

    state.queue = [...state.queue, operation]
    this._showQueue(state)
    this._persist(() => Promise.all([writeClock(userUuid, state.clock), putQueued(userUuid, operation)]))
    this._deliver(operation)

    return operation
  }

  subscribe(set: string, apply: OperationHandler): void {
    this.handlers[set] = [...(this.handlers[set] ?? []), apply]
  }

  async send(): Promise<void> {
    const userUuid = this._userUuid()
    if (userUuid === '') {
      return
    }
    const state = await this._loadedState(userUuid)
    const queue = state.queue
    if (queue.length === 0) {
      return
    }

    const response = await new PushOperationsRoute().exec(new PushOperationsParams({ operations: queue }))
    if (!response.isSuccess()) {
      return
    }

    const result = response.getData()
    const accepted = new Set(result.accepted)
    const answered = new Set([...result.accepted, ...result.rejected.map((rejected) => rejected.time)])
    // очередь берётся из состояния заново: за время запроса в неё могли попасть новые операции
    state.queue = state.queue.filter((operation) => !answered.has(operation.time))
    this._showQueue(state)
    const kept = queue.filter((operation) => accepted.has(operation.time))
    this._persist(() => Promise.all([saveOperations(userUuid, kept), removeQueued(userUuid, [...answered])]))
  }

  async receive(): Promise<void> {
    const userUuid = this._userUuid()
    if (userUuid === '') {
      return
    }
    const state = await this._loadedState(userUuid)

    for (let page = 0; page < PAGES_PER_RECEIVE; page++) {
      const response = await new GetOperationsRoute().exec(new GetOperationsParams({ after: state.cursor, limit: PAGE_SIZE }))
      if (!response.isSuccess()) {
        return
      }

      const data = response.getData()
      const received = data.operations.filter((operation) => isOperationTime(operation.time))
      received.forEach((operation) => this._moveClock(state, operation))
      state.cursor = data.next
      this._persist(() => Promise.all([saveOperations(userUuid, received), writeClock(userUuid, state.clock), writeCursor(userUuid, state.cursor)]))
      await this.persisting
      received.forEach((operation) => this._accept(operation))
      if (!data.hasMore) {
        return
      }
    }
  }

  mergeRecord(state: IRecordState, operation: IOperation): IRecordState {
    return mergeRecord(state, operation)
  }

  async getRecordOperations(set: string, record: string): Promise<IOperation[]> {
    const userUuid = this._userUuid()
    if (userUuid === '') {
      return []
    }
    const state = await this._loadedState(userUuid)
    await this.persisting
    const known = new Map<string, IOperation>()
    const stored = await readRecordOperations(userUuid, set, record)
    stored.forEach((operation) => known.set(operation.time, operation))
    state.queue
      .filter((operation) => operation.set === set && operation.record === record)
      .forEach((operation) => known.set(operation.time, operation))

    return [...known.values()].sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0))
  }

  getQueueSize(): number {
    return this.store.queueSize
  }

  start(): void {
    this.store.working = true
    removeLegacyStorage()
    const userUuid = this._userUuid()
    if (userUuid === '') {
      return
    }
    this._loadedState(userUuid).then((state) => {
      if (!this.store.working) {
        return undefined
      }
      this._showQueue(state)
      return this._exchange()
    })
  }

  stop(): void {
    this.store.working = false
  }

  /** Сначала отправка, потом получение; пока обмен идёт, второй не начинается */
  async _exchange(): Promise<void> {
    if (!this.store.working || this.exchanging) {
      return
    }
    this.exchanging = true
    try {
      await this.send()
      await this.receive()
    } finally {
      this.exchanging = false
    }
  }

  /** Состояние учётной записи в памяти; при первом обращении начинается загрузка из IndexedDB */
  _state(userUuid: string): IUserState {
    let state = this.states[userUuid]
    if (!state) {
      state = { queue: [], clock: { millis: 0, counter: 0 }, cursor: 0, loaded: Promise.resolve() }
      state.loaded = this._load(userUuid, state)
      this.states[userUuid] = state
    }

    return state
  }

  async _loadedState(userUuid: string): Promise<IUserState> {
    const state = this._state(userUuid)
    await state.loaded

    return state
  }

  /** Записанное раньше сливается с тем, что успело появиться в памяти до конца загрузки */
  async _load(userUuid: string, state: IUserState): Promise<void> {
    try {
      const [queue, clock, cursor] = await Promise.all([readQueue(userUuid), readClock(userUuid), readCursor(userUuid)])
      const inMemory = new Set(state.queue.map((operation) => operation.time))
      state.queue = [...queue.filter((operation) => !inMemory.has(operation.time)), ...state.queue]
      state.clock = moveClock(state.clock, formatOperationTime(clock.millis, clock.counter, '00000000-0000-0000-0000-000000000000'))
      state.cursor = Math.max(state.cursor, cursor)
    } catch (e) {
      console.error('sync: storage is not available', e)
    }
  }

  /** Операция от узла: подписчики получают чужие; свою операцию они уже получили в write() */
  _accept(operation: IOperation): void {
    if (operationTimeOrigin(operation.time) !== this.auth().getDeviceUuid()) {
      this._deliver(operation)
    }
  }

  _moveClock(state: IUserState, operation: IOperation): void {
    state.clock = moveClock(state.clock, operation.time)
  }

  _deliver(operation: IOperation): void {
    (this.handlers[operation.set] ?? []).forEach((apply) => apply(operation))
  }

  _showQueue(state: IUserState): void {
    this.store.queueSize = state.queue.length
  }

  /** Записи в IndexedDB идут по очереди; ошибка пишется в консоль и обмен не останавливает */
  _persist(action: () => Promise<unknown>): void {
    this.persisting = this.persisting.then(action).then(() => undefined).catch((e) => console.error('sync: storage write failed', e))
  }

  _userUuid(): string {
    return readTokenUserUuid()
  }
}

export default function (): ISysSyncModule {
  return SysSyncV1.getInstance()
}
